import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Search,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';

const DAYS_HEADER = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const HomeCalendarWidget = () => {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [searchPerson, setSearchPerson] = useState('');

  const handlePrev = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNext = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Generate month days matrix
  const getMonthMatrix = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Monday is 0, Sunday is 6
    let startingDay = firstDay.getDay() - 1;
    if (startingDay === -1) startingDay = 6;

    const totalDays = lastDay.getDate();
    const days = [];

    // Empty cells before start
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    for (let d = 1; d <= totalDays; d++) {
      days.push(d);
    }

    return days;
  };

  const year1 = currentDate.getFullYear();
  const month1 = currentDate.getMonth();

  const nextMonthDate = new Date(year1, month1 + 1, 1);
  const year2 = nextMonthDate.getFullYear();
  const month2 = nextMonthDate.getMonth();

  const daysMonth1 = getMonthMatrix(year1, month1);
  const daysMonth2 = getMonthMatrix(year2, month2);

  // Calendar highlights: blue for today, subtle gray shading for weekends
  const getDayStyle = (year, month, day) => {
    if (!day) return {};
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay(); // 0 is Sunday, 6 is Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    const isToday =
      today.getFullYear() === year &&
      today.getMonth() === month &&
      today.getDate() === day;

    if (isToday) {
      return { bgcolor: '#2081be', color: 'white', fontWeight: 'bold' };
    }

    if (isWeekend) {
      return { bgcolor: '#f1f5f9', color: '#94a3b8', fontWeight: 600 };
    }

    return { color: '#334155' };
  };

  const renderMonth = (year, month, days) => (
    <Box sx={{ flex: 1, minWidth: '180px', display: 'flex', flexDirection: 'column' }}>
      {/* Month Header Banner */}
      <Box
        sx={{
          bgcolor: '#257eb9',
          color: 'white',
          py: 0.5,
          textAlign: 'center',
          borderRadius: '10px 10px 0 0',
          fontWeight: 700,
          fontSize: '0.84rem',
          letterSpacing: 0.3,
        }}
      >
        {MONTH_NAMES[month]}
      </Box>

      {/* Days of week header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          bgcolor: '#f8fafc',
          py: 0.35,
          borderLeft: '1px solid #e2e8f0',
          borderRight: '1px solid #e2e8f0',
        }}
      >
        {DAYS_HEADER.map((d) => (
          <Typography
            key={d}
            sx={{
              fontSize: '0.72rem',
              color: '#475569',
              textAlign: 'center',
              fontWeight: 700,
            }}
          >
            {d}
          </Typography>
        ))}
      </Box>

      {/* Days Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          border: '1px solid #e2e8f0',
          borderTop: 'none',
          borderRadius: '0 0 10px 10px',
          bgcolor: '#ffffff',
          overflow: 'hidden',
          flex: 1,
        }}
      >
        {days.map((day, idx) => {
          const style = getDayStyle(year, month, day);
          return (
            <Box
              key={idx}
              sx={{
                height: { xs: 20, sm: 22, md: 24 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.74rem',
                borderRight: (idx + 1) % 7 === 0 ? 'none' : '1px solid #f1f5f9',
                borderBottom: '1px solid #f1f5f9',
                transition: 'background-color 0.15s ease',
                ...style,
              }}
            >
              {day || ''}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        borderRadius: '18px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        bgcolor: '#ffffff',
        p: { xs: 1.2, sm: 1.5, md: 1.6 },
        mt: 1,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Top Header Row */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: { xs: 1, md: 1.2 },
        }}
      >
        <Chip
          label="TU CALENDARIO"
          sx={{
            bgcolor: '#257eb9',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.78rem',
            px: 1.2,
            height: '30px',
            borderRadius: '15px',
            '&:hover': { bgcolor: '#1d6594' },
          }}
        />

        <TextField
          size="small"
          placeholder="Buscar personas"
          value={searchPerson}
          onChange={(e) => setSearchPerson(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#257eb9', fontSize: '1.1rem' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', sm: '210px' },
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
              fontSize: '0.8rem',
              height: '30px',
              borderColor: '#93c5fd',
              '& fieldset': { borderColor: '#93c5fd' },
              '&:hover fieldset': { borderColor: '#257eb9' },
            },
          }}
        />
      </Box>

      {/* Calendar Months with Nav Arrows */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flex: 1, my: 'auto', minWidth: 0 }}>
        <IconButton onClick={handlePrev} size="small" sx={{ color: '#257eb9', p: 0.5 }}>
          <ChevronLeft />
        </IconButton>

        <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, flex: 1, minWidth: 0, flexDirection: { xs: 'column', sm: 'row' } }}>
          {renderMonth(year1, month1, daysMonth1)}
          {renderMonth(year2, month2, daysMonth2)}
        </Box>

        <IconButton onClick={handleNext} size="small" sx={{ color: '#257eb9', p: 0.5 }}>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Footer: Inline Legend Items aligned with calendar */}
      <Box
        sx={{
          mt: 1.5,
          pt: 1,
          mx: { xs: 0.5, sm: 5 },
          borderTop: '1px dashed #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          gap: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box
            sx={{
              width: 13,
              height: 13,
              borderRadius: '4px',
              bgcolor: '#2081be',
              boxShadow: '0 2px 4px rgba(32, 129, 190, 0.25)',
            }}
          />
          <Typography sx={{ fontSize: '0.76rem', color: '#475569', fontWeight: 600 }}>
            Día actual
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box
            sx={{
              width: 13,
              height: 13,
              borderRadius: '4px',
              bgcolor: '#f1f5f9',
              border: '1px solid #cbd5e1',
            }}
          />
          <Typography sx={{ fontSize: '0.76rem', color: '#475569', fontWeight: 600 }}>
            Fin de semana
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default HomeCalendarWidget;
